import {
  ArrowLeftIcon,
  ChevronRightIcon,
  Lightbulb02Icon,
} from '@langchain/untitled-ui-icons';
import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { DataGrid, DataGridPagination } from '@/components/DataGrid';
import { useDataGridSizingLocalStorage } from '@/components/DataGrid.utils';
import { DataGridColumnVisibilityPopover } from '@/components/DataGrid/DataGridColumnVisibilityPopover';
import { EditableValue } from '@/components/EditableValue/EditableValue';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { Rate } from '@/components/Rate';
import { SessionLatencyCell } from '@/components/SessionsTable/components/Cell';
import { SkeletonRows } from '@/components/Skeleton';
import { TextOverflowTooltip } from '@/components/TextOverflowTooltip';
import { TotalFeedbackBar } from '@/components/TotalFeedbackBar/TotalFeedbackBar';
import { IconButton, Text } from '@/design-system';
import { useDataGridState } from '@/hooks/useDataGridState';
import { useMinMaxFeedbackValues } from '@/hooks/useMinMaxFeedbackValues/useMinMaxFeedbackValues';
import { cn } from '@/utils/tailwind';

import { useSessionClusterJob } from '../ClusterJob/hooks/useSessionClusterJobs';
import { useUpdateClusterJobName } from '../ClusterJob/hooks/useUpdateClusterJobName';
import { ProjectSidebarSlot } from '../Project/ProjectSidebar.utils';
import { CategoryDetailsPane } from './components/CategoryDetailsPane';
import { InsightsJobStats } from './components/InsightsJobStats';
import { NameDisplay } from './components/NameDisplay';
import { LeafRunsDisplay, ParentRunsDisplay } from './components/RunsDisplay';
import {
  CATEGORY_TREE_COLUMN_VISIBILITY_STATE_KEY,
  CATEGORY_TREE_COLUMN_WIDTH_STATE_KEY,
  formatter,
  usdFormatter,
} from './constants';
import { TreeNode } from './types';
import { buildCategoryTree } from './utils/buildCategoryTree';
import { flattenTreeForDisplay } from './utils/flattenTreeForDisplay';

const columnHelper = createColumnHelper<TreeNode>();

const getCategoryTreeColumnsDef = ({
  expandedIds,
  setExpandedIds,
  attributes,
  feedbackKeys,
  minMaxAttributeValues,
  minMaxFeedbackValues,
}: {
  expandedIds: Set<string>;
  setExpandedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  attributes: string[];
  feedbackKeys: string[];
  minMaxAttributeValues: Map<string, [number, number]>;
  minMaxFeedbackValues: Map<string, [number, number]>;
}): ColumnDef<TreeNode>[] => [
  columnHelper.display({
    id: 'name',
    header: () => (
      <div className="flex items-center gap-3">
        <ChevronRightIcon className="h-4 w-4" />
        <Text>Category</Text>
      </div>
    ),
    enableResizing: true,
    enableSorting: false,
    enableHiding: false,
    size: 150,
    minSize: 50,
    maxSize: 300,
    cell: ({ row }) => {
      const node = row.original;
      const isExpanded = expandedIds.has(node.id);
      return (
        <NameDisplay
          node={row.original}
          isExpanded={isExpanded}
          setExpandedIds={setExpandedIds}
        />
      );
    },
  }),
  columnHelper.display({
    id: 'description',
    header: 'Description',
    enableResizing: true,
    enableSorting: false,
    size: 300,
    minSize: 100,
    maxSize: 600,
    cell: ({ row }) => {
      const node = row.original;
      const description = node.description;
      return description ? (
        <TextOverflowTooltip
          textVariant="sm"
          textProps={{ color: 'tertiary' }}
          className={cn(
            'whitespace-pre-wrap',
            node.isLeaf && node.depth > 0 ? 'line-clamp-1' : 'line-clamp-3'
          )}
          customTooltip={description}
        >
          {description}
        </TextOverflowTooltip>
      ) : (
        <Text variant="sm" color="tertiary">
          —
        </Text>
      );
    },
  }),
  columnHelper.display({
    id: 'runs',
    header: 'Runs',
    enableResizing: true,
    size: 300,
    minSize: 100,
    maxSize: 600,
    cell: ({ row }) => {
      const node = row.original;
      if (node.isLeaf && node.depth > 0) {
        return <LeafRunsDisplay node={node} />;
      }
      return <ParentRunsDisplay node={node} />;
    },
  }),
  ...attributes.map((key) => ({
    id: `attribute_${key}`,
    header: key,
    enableResizing: true,
    enableSorting: false,
    size: 175,
    minSize: 75,
    maxSize: 300,
    cell: ({ row }) => (
      <TotalFeedbackBar
        type={key}
        feedbackStats={row.original.stats?.attribute_stats?.[key] ?? {}}
        minMax={minMaxAttributeValues.get(key)}
      />
    ),
  })),
  ...feedbackKeys.map((key) => ({
    id: `feedback_${key}`,
    header: key,
    enableResizing: true,
    enableSorting: false,
    size: 175,
    minSize: 75,
    maxSize: 300,
    cell: ({ row }) => (
      <TotalFeedbackBar
        type={key}
        feedbackStats={row.original.stats?.feedback_stats?.[key] ?? {}}
        minMax={minMaxFeedbackValues.get(key)}
      />
    ),
  })),
  columnHelper.display({
    id: 'error_rate',
    header: 'Error Rate',
    enableResizing: true,
    enableSorting: false,
    size: 80,
    minSize: 20,
    maxSize: 120,
    cell: ({ row }) => {
      const value = row.original.stats?.error_rate ?? 0;
      return <Rate value={value} />;
    },
  }),
  columnHelper.display({
    id: 'p50_latency',
    header: 'P50 Latency',
    enableResizing: true,
    enableSorting: false,
    size: 80,
    minSize: 20,
    maxSize: 120,
    cell: ({ row }) => (
      <SessionLatencyCell row={row.original.stats ?? {}} metric="latency_p50" />
    ),
  }),
  columnHelper.display({
    id: 'p99_latency',
    header: 'P99 Latency',
    enableResizing: true,
    enableSorting: false,
    size: 80,
    minSize: 20,
    maxSize: 120,
    cell: ({ row }) => (
      <SessionLatencyCell row={row.original.stats ?? {}} metric="latency_p99" />
    ),
  }),
  columnHelper.display({
    id: 'tokens_p50',
    header: 'P50 Tokens',
    enableResizing: true,
    enableSorting: false,
    size: 80,
    minSize: 20,
    maxSize: 120,
    cell: ({ row }) => (
      <Text variant="sm">
        {formatter.format(row.original.stats?.median_tokens ?? 0)}
      </Text>
    ),
  }),
  columnHelper.display({
    id: 'p50_cost',
    header: 'P50 Cost',
    enableResizing: true,
    enableSorting: false,
    size: 80,
    minSize: 20,
    maxSize: 120,
    cell: ({ row }) => (
      <Text variant="sm">
        {usdFormatter.format(row.original.stats?.cost_p50 ?? 0)}
      </Text>
    ),
  }),
];

export const InsightsJobCategories = () => {
  'use no memo';
  const { sessionId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const clusterJobId = searchParams.get('clusterJobId');

  const {
    data: insightsJob,
    isLoading,
    error,
  } = useSessionClusterJob(sessionId || null, clusterJobId || null);

  const updateClusterJobName = useUpdateClusterJobName(
    sessionId,
    clusterJobId || undefined
  );

  const [categoryIdRunsPaneOpen, setCategoryIdRunsPaneOpen] = useState<
    string | undefined
  >(undefined);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const { columnVisibilityModel, setColumnVisibilityModel } = useDataGridState({
    columnVisibilityStateKey: CATEGORY_TREE_COLUMN_VISIBILITY_STATE_KEY,
  });
  const [columnSizing, setColumnSizing] = useDataGridSizingLocalStorage(
    CATEGORY_TREE_COLUMN_WIDTH_STATE_KEY
  );

  const treeRoots = useMemo(() => {
    if (!insightsJob?.clusters) return [];
    return buildCategoryTree(insightsJob.clusters);
  }, [insightsJob?.clusters]);

  const displayData = useMemo(() => {
    return flattenTreeForDisplay(treeRoots, expandedIds);
  }, [treeRoots, expandedIds]);

  const allFeedbackKeys = useMemo(() => {
    return Array.from(
      new Set(
        insightsJob?.clusters?.flatMap(
          (c) => Object.keys(c.stats?.feedback_stats ?? {}) ?? []
        )
      )
    );
  }, [insightsJob?.clusters]);

  const minMaxFeedbackValues = useMinMaxFeedbackValues(
    insightsJob?.clusters?.map((c) => ({
      feedback_stats: c.stats?.feedback_stats ?? {},
    })) ?? []
  );

  const allAttributes = useMemo(() => {
    return Array.from(
      new Set(
        insightsJob?.clusters?.flatMap(
          (c) => Object.keys(c.stats?.attribute_stats ?? {}) ?? []
        )
      )
    );
  }, [insightsJob?.clusters]);

  const minMaxAttributeValues = useMinMaxFeedbackValues(
    insightsJob?.clusters?.map((c) => ({
      feedback_stats: c.stats?.attribute_stats ?? {},
    })) ?? []
  );

  const columns = useMemo(() => {
    return getCategoryTreeColumnsDef({
      expandedIds,
      setExpandedIds,
      attributes: allAttributes,
      feedbackKeys: allFeedbackKeys,
      minMaxAttributeValues,
      minMaxFeedbackValues,
    });
  }, [
    expandedIds,
    setExpandedIds,
    allAttributes,
    allFeedbackKeys,
    minMaxAttributeValues,
    minMaxFeedbackValues,
  ]);

  const table = useReactTable({
    columns,
    data: displayData,
    state: {
      columnSizing,
      columnVisibility: columnVisibilityModel,
    },
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibilityModel,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualExpanding: true,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    columnResizeMode: 'onChange',
  });

  const handleRowClick = (node: TreeNode, event: React.MouseEvent) => {
    event.preventDefault();

    if (node.isLeaf) {
      setCategoryIdRunsPaneOpen(node.id);
    } else {
      setExpandedIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(node.id)) {
          newSet.delete(node.id);
        } else {
          newSet.add(node.id);
        }
        return newSet;
      });
    }
  };

  let errorMessage: string | null = null;

  if (error) {
    errorMessage = error.message;
  } else if (!clusterJobId) {
    errorMessage = 'No cluster job selected';
  } else if (!insightsJob) {
    errorMessage = 'Cluster job not found';
  } else if (!sessionId) {
    errorMessage = 'No session ID found';
  }

  const insightsJobStats = useMemo(() => {
    if (!insightsJob) return undefined;
    return {
      status: insightsJob.status,
      startTime: insightsJob.start_time,
      endTime: insightsJob.end_time,
      runsSampled: treeRoots
        .map((node) => node.num_runs)
        .reduce((a, b) => (a || 0) + (b || 0), 0),
      attributes: insightsJob.metadata?.attribute_schemas,
      summaryPrompt: insightsJob.metadata?.summary_prompt,
    };
  }, [insightsJob, treeRoots]);

  if ((!insightsJob || !sessionId || !clusterJobId) && !isLoading) {
    return (
      <div className="-mx-4 -my-4 space-y-4 p-4 text-left">
        <ExpandableErrorAlert
          error={
            errorMessage ??
            insightsJob?.error ??
            'An error occurred during this Insights job'
          }
        />
      </div>
    );
  }

  return (
    <>
      <div className="m-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <IconButton
            variant="plain"
            color="secondary"
            size="sm"
            onClick={() => {
              const newParams = new URLSearchParams(searchParams);
              newParams.delete('clusterJobId');
              newParams.delete('clusterId');
              newParams.delete('selectedLevel');
              setSearchParams(newParams);
            }}
            icon={ArrowLeftIcon}
            label="Back to Insights Jobs"
          />
          <Lightbulb02Icon className="h-4 w-4" />
          {isLoading ? (
            <SkeletonRows rows={1} className="w-[300px]" />
          ) : (
            <EditableValue
              value={insightsJob?.name || ''}
              onSubmit={(newName) => {
                updateClusterJobName({ json: { name: newName } });
              }}
              textClassProp="text-base font-medium leading-tight tracking-tight"
              inputTextClassProp="w-[300px]"
            />
          )}
        </div>
        <DataGridColumnVisibilityPopover
          table={table}
          localStorageKey={CATEGORY_TREE_COLUMN_WIDTH_STATE_KEY}
        />
      </div>

      <div className="overflow-y-auto border-b border-secondary">
        <DataGrid
          table={table}
          isLoading={isLoading}
          stickyLeftColumn={true}
          getCellHeight={(node: TreeNode) =>
            node.isLeaf && node.depth > 0 ? 40 : 80
          }
          emptyState={
            <div className="-mx-4 -my-4 space-y-4 p-4 text-left">
              {(insightsJob?.status === 'failed' ||
                insightsJob?.status === 'error') && (
                <ExpandableErrorAlert
                  error={
                    insightsJob.error ||
                    'An error occurred while processing this job'
                  }
                />
              )}
            </div>
          }
          onClick={handleRowClick}
          highlightedId={categoryIdRunsPaneOpen}
        />
      </div>

      <ProjectSidebarSlot.Fill>
        <InsightsJobStats stats={insightsJobStats} isLoadingStats={isLoading} />
      </ProjectSidebarSlot.Fill>

      <div className="mx-4 mb-4 flex items-center justify-end">
        <DataGridPagination table={table} />
      </div>

      {sessionId && clusterJobId && insightsJob && (
        <CategoryDetailsPane
          onClose={() => {
            setCategoryIdRunsPaneOpen(undefined);
            // Close run details pane
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('peek');
            newParams.delete('peeked_trace');
            setSearchParams(newParams);
          }}
          sessionId={sessionId}
          clusterJobId={clusterJobId}
          jobTimeRange={{
            start_time: insightsJob.start_time,
            end_time: insightsJob.end_time,
          }}
          category={
            categoryIdRunsPaneOpen
              ? displayData.find((node) => node.id === categoryIdRunsPaneOpen)
              : undefined
          }
          attributes={Object.keys(
            insightsJob.metadata?.attribute_schemas ?? {}
          )}
        />
      )}
    </>
  );
};
